// src/utils/audienceUtils.ts

import { DailyEngagement } from '../types/audience';

export interface PeriodComparison {
  currentTotal: number;
  previousTotal: number;
  percentageChange: number | null;  // null when previous is 0
  displayText: string;
  colorClass: string;
}

/**
 * Calculate comparison between current and previous period from daily trends
 * @param dailyTrends - Array of daily engagement data (should be 2x the period length)
 * @param valueKey - Which property to sum ('totalViewers' or 'totalEngagementSeconds')
 */
export function calculatePeriodComparison(
  dailyTrends: DailyEngagement[],
  valueKey: 'totalViewers' | 'totalEngagementSeconds'
): PeriodComparison {
  if (!dailyTrends || dailyTrends.length === 0) {
    return {
      currentTotal: 0,
      previousTotal: 0,
      percentageChange: null,
      displayText: 'No data yet',
      colorClass: 'text-gray-500'
    };
  }

  // Split into two halves: older = previous, newer = current
  const midpoint = Math.floor(dailyTrends.length / 2);
  const previousTrends = dailyTrends.slice(0, midpoint);
  const currentTrends = dailyTrends.slice(midpoint);

  const previousTotal = previousTrends.reduce((sum, d) => sum + d[valueKey], 0);
  const currentTotal = currentTrends.reduce((sum, d) => sum + d[valueKey], 0);

  // Handle edge cases
  if (previousTotal === 0 && currentTotal === 0) {
    return {
      currentTotal: 0,
      previousTotal: 0,
      percentageChange: null,
      displayText: 'No data yet',
      colorClass: 'text-gray-500'
    };
  }

  if (previousTotal === 0 && currentTotal > 0) {
    return {
      currentTotal,
      previousTotal: 0,
      percentageChange: null,
      displayText: 'New',
      colorClass: 'text-green-600'
    };
  }

  // Calculate percentage change
  const percentageChange = ((currentTotal - previousTotal) / previousTotal) * 100;
  const isPositive = percentageChange >= 0;

  return {
    currentTotal,
    previousTotal,
    percentageChange,
    displayText: `${isPositive ? '↑' : '↓'} ${Math.abs(percentageChange).toFixed(1)}% vs last period`,
    colorClass: isPositive ? 'text-green-600' : 'text-red-600'
  };
}
